/* eslint-disable no-async-promise-executor */
import Search from '@react/search/components/Search';
import {
  constructCmsSearchUrl,
  filterLinkPath,
  getApiFilters,
  getPagination,
  getSearchParams,
} from '@react/search/helpers';
import { CARE_TYPES_OBJECT } from '@sly/core/constants/careTypes';
import { AL, CLEARABLE_FILTERS, TOC } from '@sly/core/constants/filters';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePrefetch, useQuery } from 'sly/services/api';
import { useUser } from 'sly/services/auth';
import { useEvents } from 'sly/services/events';

const SearchPage = ({ showHeaderPhNo = true, chartSvgs }) => {
  const location = useLocation();
  const router = useRouter();
  const { setSegmentIds, events } = useEvents();
  let { toc: care, city, state } = router.query;

  const currentFilters = useMemo(
    () => getSearchParams(router.query),
    [router.query]
  );

  const apiFilters = getApiFilters(currentFilters);
  const { user } = useUser();
  const {
    requestInfo: { result: rawUuidAux, normalized: uuidAux },
    fetch: fetchUuidAux,
  } = usePrefetch(
    'getUuidAux',
    { id: user?.uuidAux?.id },
    { shouldBail: !user?.uuidAux?.id }
  );

  const { requestInfo: experimentRequestInfo } = usePrefetch(
    'getCmsSearchPage',
    {
      forUrl: constructCmsSearchUrl(router.query),
    }
  );
  const { data: experiments, segmentIds } = experimentRequestInfo?.result || {};

  const { requestInfo: geoGuideRequestInfo } = usePrefetch(
    'getGeoGuides',
    {
      city: currentFilters.city,
      state: currentFilters.state,
      toc: currentFilters.toc,
      include: 'curatedCommunities',
    },
    {
      encode: false,
      shouldBail: care === CARE_TYPES_OBJECT.seniorLiving,
    }
  );

  const updateUuidAux = useQuery('updateUuidAux');

  useEffect(() => {
    const { city, state, toc: care, county } = currentFilters;
    if (segmentIds?.length > 0) {
      setSegmentIds(segmentIds);
    } else {
      setSegmentIds([]);
    }

    events.page('Search', {
      city,
      state,
      care,
      county,
    });

    () => {
      setSegmentIds(null);
    };
  }, [currentFilters, events, router.query, segmentIds, setSegmentIds]);

  const { requestInfo: requestResult } = usePrefetch(
    'getSearchPage',
    apiFilters
  );

  // set the state to avoid blank page during fetch
  const [requestInfo, setRequestInfo] = useState(requestResult);
  useEffect(() => {
    if (requestResult.hasFinished && requestInfo !== requestResult) {
      setRequestInfo(requestResult);
    }
  }, [requestInfo, requestResult]);

  const onFilterChange = useCallback(
    (param, value) => {
      router.push(
        filterLinkPath(currentFilters, {
          [param]: value,
        }),
        undefined,
        { shallow: true }
      );
    },
    [currentFilters, router]
  );

  const onClearFilters = useCallback(
    (filtersToClear = CLEARABLE_FILTERS) => {
      const nextFilters = Object.entries(currentFilters).reduce(
        (acc, [key, value]) => {
          if (!filtersToClear.includes(key)) {
            acc[key] = value;
          } else if (key === TOC) {
            acc[key] = AL;
          }
          return acc;
        },
        {}
      );
      if (uuidAux?.uuidInfo) {
        fetchUuidAux();
      }
      if (nextFilters.geo && Array.isArray(nextFilters.geo)) {
        nextFilters.geo = nextFilters.geo.join(',');
      }

      router.push(filterLinkPath({}, nextFilters), undefined, {
        shallow: true,
      });
    },
    [uuidAux, rawUuidAux, updateUuidAux, currentFilters, router, fetchUuidAux]
  );

  const pagination = useMemo(
    () => getPagination(requestInfo.meta, location, currentFilters),
    [currentFilters, location, requestInfo.meta]
  );

  if (currentFilters.geo && Array.isArray(currentFilters.geo)) {
    currentFilters.geo = currentFilters.geo.join(',');
  }

  const geoGuide = geoGuideRequestInfo?.normalized?.[0] || {};

  return (
    <Search
      currentFilters={currentFilters}
      onFilterChange={onFilterChange}
      onClearFilters={onClearFilters}
      entities={requestInfo.normalized || []}
      meta={requestInfo.meta || {}}
      pagination={pagination}
      location={location}
      hasFinished={requestInfo.hasFinished}
      experiments={experiments || {}}
      geoGuide={geoGuide}
      showHeaderPhNo={showHeaderPhNo}
      chartSvgs={chartSvgs}
    />
  );
};

export default SearchPage;
